import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, Play } from 'lucide-react';
export function Hero() {
  return <section className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden bg-white" data-id="element-166">
      {/* Sinusoidal Wave Background Elements */}
      <div className="absolute inset-0 z-0 pointer-events-none opacity-30" data-id="element-167">
        <svg className="absolute top-0 left-0 w-full h-full" viewBox="0 0 1440 800" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-168">
          <motion.path initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 1
        }} transition={{
          duration: 3,
          ease: 'easeInOut'
        }} d="M-100 400C100 300 300 500 500 400C700 300 900 500 1100 400C1300 300 1500 500 1700 400" stroke="url(#gradient-teal)" strokeWidth="2" className="animate-wave" data-id="element-169" />
          <motion.path initial={{
          pathLength: 0,
          opacity: 0
        }} animate={{
          pathLength: 1,
          opacity: 0.6
        }} transition={{
          duration: 4,
          ease: 'easeInOut',
          delay: 0.5
        }} d="M-100 450C100 350 300 550 500 450C700 350 900 550 1100 450C1300 350 1500 550 1700 450" stroke="url(#gradient-coral)" strokeWidth="1.5" className="animate-wave" style={{
          animationDelay: '-2s'
        }} data-id="element-170" />
          <defs data-id="element-171">
            <linearGradient id="gradient-teal" x1="0" y1="0" x2="1440" y2="0" gradientUnits="userSpaceOnUse" data-id="element-172">
              <stop stopColor="#008C8C" stopOpacity="0" data-id="element-173" />
              <stop offset="0.5" stopColor="#008C8C" data-id="element-174" />
              <stop offset="1" stopColor="#008C8C" stopOpacity="0" data-id="element-175" />
            </linearGradient>
            <linearGradient id="gradient-coral" x1="0" y1="0" x2="1440" y2="0" gradientUnits="userSpaceOnUse" data-id="element-176">
              <stop stopColor="#F57B6C" stopOpacity="0" data-id="element-177" />
              <stop offset="0.5" stopColor="#F57B6C" data-id="element-178" />
              <stop offset="1" stopColor="#F57B6C" stopOpacity="0" data-id="element-179" />
            </linearGradient>
          </defs>
        </svg>
      </div>

      {/* Decorative Blobs */}
      <div className="absolute top-1/4 -left-20 w-96 h-96 bg-primary/5 rounded-full blur-3xl animate-pulse" data-id="element-180" />
      <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-secondary/5 rounded-full blur-3xl animate-pulse" style={{
      animationDelay: '2s'
    }} data-id="element-181" />

      <div className="container mx-auto px-6 relative z-10" data-id="element-182">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-183">
          {/* Content Column */}
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-184">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-primary text-sm font-bold mb-8" data-id="element-185">
              <span className="relative flex h-2 w-2" data-id="element-186">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" data-id="element-187"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" data-id="element-188"></span>
              </span>
              Premium Home Healthcare in Maryland
            </div>

            <h1 className="text-5xl lg:text-7xl font-heading font-bold text-dark-text leading-[1.1] mb-8" data-id="element-189">
              Enriching Lives Through{' '}
              <span className="text-primary italic" data-id="element-190">Skilled</span> &
              Compassionate Care
            </h1>

            <p className="text-xl text-body-text leading-relaxed mb-10 max-w-xl" data-id="element-191">
              Experience the comfort of home while receiving exceptional
              personalized healthcare designed around your unique needs.
            </p>

            <div className="flex flex-wrap gap-5 mb-12" data-id="element-192">
              <button className="px-8 py-4 bg-primary text-white font-bold rounded-full hover:bg-primary-dark transition-all duration-300 shadow-xl hover:shadow-primary/20 hover:-translate-y-1 flex items-center gap-2" data-id="element-193">
                Schedule an Assessment
                <ArrowRight className="w-5 h-5" data-id="element-194" />
              </button>
              <button className="px-8 py-4 bg-white text-dark-text font-bold rounded-full border border-gray-200 hover:border-primary hover:text-primary transition-all duration-300 flex items-center gap-2" data-id="element-195">
                Explore Services
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="grid grid-cols-2 gap-6" data-id="element-196">
              {['Licensed Professionals', 'Personalized Care Plans', 'Maryland Coverage', 'Compassion First'].map((item, index) => <motion.div key={item} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.5 + index * 0.1
            }} className="flex items-center gap-3 text-body-text font-medium" data-id="element-197">
                  <div className="flex-shrink-0 w-6 h-6 bg-success/10 rounded-full flex items-center justify-center" data-id="element-198">
                    <CheckCircle2 className="w-4 h-4 text-success" data-id="element-199" />
                  </div>
                  {item}
                </motion.div>)}
            </div>
          </motion.div>

          {/* Image Column */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1,
          ease: 'easeOut'
        }} className="relative" data-id="element-200">
            {/* Main Image Container */}
            <div className="relative z-10 rounded-[40px] overflow-hidden shadow-2xl border-[12px] border-white" data-id="element-201">
              <img src="https://www.harmonyhealthcaremd.com/wp-content/themes/harmonyhealthcarees376/images/slider/desktop-1.webp" alt="Compassionate Care" className="w-full h-[600px] object-cover" data-id="element-202" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" data-id="element-203" />
            </div>

            {/* Floating Glass Cards */}
            <motion.div animate={{
            y: [0, -15, 0]
          }} transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -top-10 -right-10 z-20 p-6 bg-white/80 backdrop-blur-md rounded-3xl shadow-xl border border-white/40 max-w-[240px]" data-id="element-204">
              <div className="flex items-center gap-4 mb-3" data-id="element-205">
                <div className="w-12 h-12 bg-secondary/20 rounded-2xl flex items-center justify-center" data-id="element-206">
                  <span className="text-secondary font-bold text-xl" data-id="element-207">15+</span>
                </div>
                <div className="text-sm font-bold text-dark-text" data-id="element-208">
                  Years of Excellence
                </div>
              </div>
              <p className="text-xs text-body-text leading-relaxed" data-id="element-209">
                Providing trusted healthcare services across Maryland since
                2009.
              </p>
            </motion.div>

            <motion.div animate={{
            y: [0, 15, 0]
          }} transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1
          }} className="absolute -bottom-10 -left-10 z-20 p-6 bg-white/80 backdrop-blur-md rounded-3xl shadow-xl border border-white/40 flex items-center gap-4" data-id="element-210">
              <div className="w-14 h-14 bg-primary rounded-full flex items-center justify-center text-white shadow-lg shadow-primary/30" data-id="element-211">
                <Play className="w-6 h-6 fill-current" data-id="element-212" />
              </div>
              <div data-id="element-213">
                <div className="text-sm font-bold text-dark-text" data-id="element-214">
                  Watch Our Story
                </div>
                <div className="text-xs text-body-text" data-id="element-215">See how we care</div>
              </div>
            </motion.div>

            {/* Decorative Elements */}
            <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] border border-primary/10 rounded-full" data-id="element-216" />
            <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] border border-secondary/5 rounded-full" data-id="element-217" />
          </motion.div>
        </div>
      </div>
    </section>;
}